import { useCallback, useMemo } from "react";
import { Box, Backdrop, Card, CardContent, Dialog, Typography, IconButton } from "@mui/material";
import { makeStyles, styled } from "@mui/styles";
import * as Yup from "yup";
import { Close } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";

import dayjs from "dayjs";
import axios from "../utils/axios";
import IndividualActivityForm from "./IndividualActivityForm";
import { setProfile } from "../reducers/profile";

const cardPadding = { padding: [0, 2, 4] };

const useStyles = makeStyles(() => ({
  cardContent: {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
  },
}));

export default function LogAnActivity({ open, onClose, initialValuesToDuplicate }) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  const initialValues = useMemo(
    () => ({
      title: "",
      goal: null,
      activity_type: null,
      what_did_you_do: "",
      what_did_you_learn: "",
      ai_summary: "",
      location: null,
      address: "",
      sessions: [{ date: null, hours: "" }],
      skills: [],
      ...initialValuesToDuplicate,
    }),
    [initialValuesToDuplicate]
  );

  const countWords = (text = "") => text.trim().split(/\s+/).filter(word => word.length > 0).length;

  const LogActivitySchema = useCallback(
    () =>
      Yup.object().shape({
        title: Yup.string().min(1, "Too Short!").max(50, "Too Long!").required("Required"),
        goal: Yup.object()
          .required("Please choose a goal")
          .shape({ id: Yup.number().required("Please choose a goal") })
          .nullable(),
        activity_type: Yup.object()
          .shape({ id: Yup.number() })
          .nullable(),
        what_did_you_do: Yup.string()
          .required("Required")
          .test('max-words', 'Maximum 50 words allowed', val => countWords(val) <= 50),
        what_did_you_learn: Yup.string()
          .required("Required")
          .test('max-words', 'Maximum 50 words allowed', val => countWords(val) <= 50),
        ai_summary: Yup.string()
          .required("Please generate an AI summary"),
        location: Yup.object()
          .required("Please choose a region")
          .shape({ id: Yup.number().required("Please choose a region") })
          .nullable(),
        sessions: Yup.array().of(
          Yup.object().shape({
            date: Yup.date().typeError("Please enter a date")
              .min(dayjs().subtract(6, "month"), "Session must be within the last 6 months")
              .max(dayjs(), "Session must be in the past")
              .required("Date is required"),
            hours: Yup.number()
              .required("Hours is required")
              .min(1, "Minimum 1 hour")
              .max(8, "Maximum 8 hours"),
          })
        ).min(1, "At least one session is required"),
        skills: Yup.array().of(Yup.object().shape({ id: Yup.number() })),
      }),
    []
  );

  const handleSubmit = useCallback(
    async (values) => {
      const {
        sessions,
        title,
        skills,
        location,
        goal,
        address,
        activity_type: activityType,
        what_did_you_do,
        what_did_you_learn,
        ai_summary,
        beneficiary,
      } = values;

      const firstDate = sessions[0].date;
      const totalHours = sessions.reduce((sum, s) => sum + Number(s.hours || 0), 0);

      // Set time to 00:00:00 for start datetime
      const startDateTime = dayjs(firstDate)
        .hour(0)
        .minute(0)
        .second(0);

      // Calculate end datetime by adding total hours
      const endDateTime = startDateTime.add(totalHours, "hours");

      const obj = {
        mode: "individual",
        title,
        description: ai_summary,
        goal_id: goal?.id,
        location_id: location?.id,
        activity_type_id: activityType?.id,
        what_did_you_do,
        what_did_you_learn,
        ai_summary,
        ai_summary_tagged: true,
        beneficiary_id: beneficiary?.id || null,
        activity_date: dayjs(firstDate).format("YYYY-MM-DD"),
        starts: startDateTime.toISOString(),
        ends: endDateTime.toISOString(),
        skills: (skills || []).map((s) => ({ id: s.id })),
        address,
        sessions: sessions.map((s) => ({
          date: dayjs(s.date).format("YYYY-MM-DD"),
          hours: Number(s.hours),
        })),
      };
      await axios.post("activities", obj);

      // Refresh profile to update completed_activity_ids, hours, and awards
      const profileResponse = await axios.get("auth/profile");
      dispatch(setProfile(profileResponse.data.data));

      enqueueSnackbar("Activity logged successfully", { variant: "success" });
      onClose(true);
    },
    [onClose, enqueueSnackbar, dispatch]
  );

  return (
    <Dialog
      BackdropComponent={styled(Backdrop, {
        name: "MuiModal",
        slot: "Backdrop",
        overridesResolver: (props, styles) => styles.backdrop,
      })({ zIndex: -1 })}
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      scroll="body"
      transitionDuration={{ appear: 0 }}
    >
      <Card sx={cardPadding}>
        <CardContent className={classes.cardContent}>
          <Box width="100%" display="flex" flexDirection="row" mb={1}>
            <Box flexGrow={1} />
            <Typography sx={{ fontSize: 28, fontWeight: 800 }}>Log an activity</Typography>
            <Box flexGrow={1} justifyContent="flex-end" display="flex">
              <IconButton onClick={onClose}>
                <Close color="primary" />
              </IconButton>
            </Box>
          </Box>
          <IndividualActivityForm initialValues={initialValues} LogActivitySchema={LogActivitySchema} handleSubmit={handleSubmit} />
        </CardContent>
      </Card>
    </Dialog>
  );
}

LogAnActivity.defaultProps = {
  initialValuesToDuplicate: {},
};

LogAnActivity.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  initialValuesToDuplicate: PropTypes.shape({}),
};
